// vite sets import.meta.env.DEV/PROD automatically based on `vite dev` vs `vite build` -
// no manual toggling, no risk of shipping a build pointed at localhost
export const HOST = import.meta.env.DEV ? 'http://localhost:9292' : 'https://wthr.cloud/api';

export let RETRY_DELAY = 1000; // ms

export function setRetryDelay(delay: number) {
  RETRY_DELAY = delay;
}

export async function get<T>(route: string, retry=0, fetch=window.fetch) {
  while (retry >= 0) {
    console.log(`[api] fetch ${route}`);

    try {
      const resp = await fetch(route, {
        headers: {
          Accept: 'application/ld+json',
        }

      });

      if (!resp.ok) {
        throw new Error(`request ${route} failed with ${resp.status}`)
      }

      return resp.json() as T;
    } catch(e) {
      if (retry <= 0) {
        console.log(`[api] retries exhausted -${e} - ${route}`);
        return Promise.reject(e);
      }

      await new Promise(r => setTimeout(r, RETRY_DELAY));

      console.log(`[api] retry -${e} - ${route}`);
    } finally {
      retry--;
    }
  }

  return Promise.reject();
}

interface IPosition {
  coords: {
    latitude: number,
    longitude: number
  }
}

interface IPointsResponse {
  relativeLocation: string
  gridId: string
  radarStation: string
  forecast: string
  forecastHourly: string
  latitude: number
  longitude: number
}

export interface IAlert {
  id: string
  headline: string
  expires: string
  description: string
  severity: 'Extreme'| 'Severe' | 'Moderate' | 'Minor' | 'Unknown'
}

export interface IAlertsResponse {
  "@graph": IAlert[]
}

interface ICurrentResponse {
  generatedAt: string;
  periods: any[];
}

interface IHourlyResponse {
  periods: any[];
}

export interface IConditionSummary {
  temperature: number
  high: number
  low: number
  shortForecast: string
  city: string
  state: string
}

export interface IForecast {
  relativeLocation: string
  gridId: string
  radarStation: string
  latitude: number | string
  longitude: number | string
  current: ICurrentResponse
  hourly: IHourlyResponse
  alerts: IAlertsResponse
  conditions: IConditionSummary
}

// fetchForecast resolves a forecast for pos, or for the server's own geoip-resolved
// default location when pos is omitted - the client doesn't know its own position in
// that case, so the resolved latitude/longitude comes back from the server response.
export async function fetchForecast(pos?: IPosition): Promise<IForecast> {
  const query = pos ? `?latitude=${pos.coords.latitude}&longitude=${pos.coords.longitude}` : '';

  const resp = await get<IPointsResponse>(`${HOST}/forecast${query}`, 1);
  return {
    relativeLocation: resp.relativeLocation.properties,
    gridId: resp.gridId,
    radarStation: resp.radarStation,
    latitude: resp.latitude,
    longitude: resp.longitude,
    current: resp.current.properties,
    hourly: resp.hourly.properties,
    alerts: resp.alerts,
    conditions: resp.conditions,
  }
}
